// Pomoc s textem (kap. 34): tlačítko „AI“ u textových polí s nabídkou úprav, dialog s návrhem (Nahradit / Vložit / Kopírovat / Znovu).
// Všechny modely volá server (AI brána); klient jen ukazuje, kam data šla (offline / online). Nic se nemění bez kliknutí.
import { core, ApiError } from '../api';
import type { AiBoot } from '../contract';
import { h } from '../dom';
import { t, tk } from '../i18n';
import { icon } from '../icons';
import { IwMenu, type MenuItem } from './menu';
import { dialog, prompt } from './dialog';
import { toast } from './toast';

let boot: AiBoot = { enabled: false, assistant: false, online: false };
let userOptOut = false;

export function configureAi(b: AiBoot | undefined, prefs: Record<string, unknown>): void {
  boot = b ?? { enabled: false, assistant: false, online: false };
  userOptOut = prefs['ai.enabled'] === false;
}

export function setAiOptOut(off: boolean): void {
  userOptOut = off;
  document.documentElement.classList.toggle('ai-off', !aiEnabled());
}

export function aiEnabled(): boolean {
  return boot.enabled && !userOptOut;
}

export function aiInfo(): AiBoot {
  return boot;
}

const EDIT_ACTIONS = ['improve', 'shorten', 'expand', 'formal', 'informal', 'translate', 'summarize', 'bullets'] as const;
const VIEW_ACTIONS = ['summarize', 'translate', 'bullets'] as const;
type TextAction = (typeof EDIT_ACTIONS)[number];

export interface AiResult {
  text: string;
  online: boolean;
  provider: string;
}

/** Volání obecné textové akce přes AI bránu. */
export async function aiText(action: TextAction, text: string, targetLang?: string): Promise<AiResult> {
  return core<AiResult>('ai', 'text', { action, text, targetLang });
}

/**
 * Dialog s návrhem: původní text vlevo (zkráceně), návrh vpravo, tlačítka podle toho, zda lze do pole zapsat.
 * Vrátí 'replace' | 'insert' | null; při 'retry' se akce zavolá znovu.
 */
export async function aiSuggest(title: string, original: string, run: () => Promise<AiResult>, canWrite: boolean): Promise<{ mode: 'replace' | 'insert'; text: string } | null> {
  const result = h('div', { class: 'ai-result' }, h('div', { class: 'ai-working' }, icon('sparkles', 'icon spin'), ' ', t('core.ai.working')));
  const note = h('div', { class: 'ai-note muted small' });
  const body = h('div', { class: 'ai-dialog' },
    original ? h('div', { class: 'ai-original' }, h('div', { class: 'ai-label' }, t('core.ai.original')), h('div', { class: 'ai-text muted' }, original.length > 600 ? original.slice(0, 600) + '…' : original)) : null,
    h('div', { class: 'ai-label' }, t('core.ai.result')),
    result,
    note,
  );
  let text = '';
  const load = async () => {
    result.replaceChildren(h('div', { class: 'ai-working' }, icon('sparkles', 'icon spin'), ' ', t('core.ai.working')));
    note.textContent = '';
    try {
      const r = await run();
      text = r.text;
      result.replaceChildren(h('div', { class: 'ai-text' }, text));
      note.replaceChildren(icon(r.online ? 'globe' : 'shield', 'icon'), ' ', r.online ? t('core.ai.online_note', { provider: r.provider }) : t('core.ai.offline_note'));
    } catch (e) {
      text = '';
      result.replaceChildren(h('div', { class: 'ai-error' }, tk((e as ApiError).message, (e as ApiError).params ?? undefined)));
    }
  };
  void load();
  for (;;) {
    const buttons = [
      { id: 'cancel', label: t('core.ui.cancel') },
      { id: 'retry', label: t('core.ai.retry') },
      { id: 'copy', label: t('core.ai.copy') },
      ...(canWrite ? [{ id: 'insert', label: t('core.ai.insert') }, { id: 'replace', label: t('core.ai.replace'), primary: true }] : []),
    ];
    const res = await dialog({ title, icon: 'sparkles', message: body, buttons, width: 640 });
    if (res.id === 'retry') {
      void load();
      continue;
    }
    if (res.id === 'copy') {
      try { await navigator.clipboard.writeText(text); toast(t('core.ui.copied'), 'ok'); } catch { /* bez schránky */ }
      continue;
    }
    if ((res.id === 'replace' || res.id === 'insert') && text) return { mode: res.id, text };
    return null;
  }
}

/**
 * Tlačítko „AI“ pro textové pole. getText/setText pracují s hodnotou pole; editable() říká, zda se smí zapisovat.
 */
export function aiFieldButton(getText: () => string, setText: (v: string) => void, editable: () => boolean): HTMLElement {
  const btn = h('button', { type: 'button', class: 'ai-btn', title: t('core.ai.title') }, icon('sparkles'), h('span', null, t('core.ai.button')));
  btn.addEventListener('click', (e) => {
    e.preventDefault();
    const text = getText().trim();
    if (!text) {
      toast(t('core.ai.empty'), 'info');
      return;
    }
    const actions: readonly TextAction[] = editable() ? EDIT_ACTIONS : VIEW_ACTIONS;
    const items: MenuItem[] = actions.map((a) => ({
      label: t('core.ai.' + a),
      icon: a === 'translate' ? 'globe' : a === 'improve' ? 'check' : a === 'summarize' || a === 'bullets' ? 'list' : 'text',
      onSelect: () => void runAction(a),
    }));
    const r = btn.getBoundingClientRect();
    IwMenu.show(items, r.left, r.bottom + 2);
  });
  const runAction = async (a: TextAction) => {
    let lang: string | undefined;
    if (a === 'translate') {
      const l = await prompt(t('core.ai.translate_prompt'), 'angličtina');
      if (!l) return;
      lang = l;
    }
    const original = getText();
    const picked = await aiSuggest(t('core.ai.' + a), original, () => aiText(a, original, lang), editable());
    if (!picked) return;
    setText(picked.mode === 'replace' ? picked.text : original.replace(/\s*$/, '') + '\n\n' + picked.text);
  };
  return btn;
}
